
import React from 'react'
import { compose } from 'widgets-shared/core'
import { withComponentContext } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'
import { Grid, Typography } from '@mui/material'
import BookmakersAndLogos from './../BookmakersAndLogos/BookmakersAndLogos'
import FixturesList from './../FixturesList/FixturesList'
import { alpha } from '@mui/material/styles'

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { OddsFormat, OddsComparisonGridConfig, FixtureData, CompetitionData } from './../types'
import { Bookmakers } from 'smartstream-widgets/types/sharedModule/core'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

type Props = {
  bookmakers: Bookmakers,
  isBookmakerLinkEnabled: boolean,
  classes: {
    [K in string]: string
  },
  config: OddsComparisonGridConfig,
  dataNodes: Array<FixtureData & CompetitionData>,
  inView: string,
  oddsFormat: OddsFormat,
  showMoreHandler?: ((arg0: string) => void),
  translations: {
    [K in string]: string
  }
}

const styles = (theme: Theme) => createStyles({
  competitorName: {
    fontWeight: 700
  },
  dash: {
    backgroundColor: theme.palette.text.secondary,
    height: 1,
    width: 14
  },
  dateCaption: {
    fontSize: 9,
    marginLeft: 14,
    lineHeight: 1.4
  },
  footer: {
    height: 56,
    paddingRight: 40
  },
  gameLabel: {
    alignItems: 'center',
    alignSelf: 'stretch',
    color: theme.palette.text.secondary,
    display: 'flex',
    background: theme.palette.background.paper,
    boxSizing: 'border-box',
    flexShrink: 0,
    fontSize: 11,
    left: 0,
    paddingLeft: 15,
    position: 'sticky',
    top: 0,
    width: 170,
    '&::after': {
      background: `linear-gradient(to right, ${theme.palette.background.paper}, ${alpha(theme.palette.background.paper, 0)})`,
      content: '\'\'',
      display: 'block',
      height: '100%',
      position: 'absolute',
      right: -15,
      top: 0,
      width: 15,
    }
  },
  signUpLabel: {
    display: 'none',
    fontSize: 12,
    paddingLeft: 15,
    color: theme.palette.text.secondary
  },
  gridHeaderContainer: {
    backgroundColor: theme.palette.divider,
    height: 34,
    left: 0,
    position: 'sticky'
  },
  header: {
    boxSizing: 'border-box',
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    minHeight: 60,
    minWidth: '100%',
    padding: `8px 0px`,
    width: 'min-content'
  },
  headerLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginLeft: 14,
    marginRight: 10
  },
  root: {
    overflowX: 'auto'
  },
  scoreBoardRow: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:nth-child(even)': {
      backgroundColor: theme.palette.background.default,
      '& $verticalScoreBoard::after': {
        background: `linear-gradient(to right, ${theme.palette.background.default}, ${alpha(theme.palette.background.default, 0)})`
      }
    }
  },
  showMore: {
    color: theme.palette.primary.main,
    display: 'flex',
    left: 0,
    position: 'sticky',
    top: 0,
    width: '100%'
  },
  verticalScoreBoard: {
    '&::after': {
      background: `linear-gradient(to right, ${theme.palette.background.paper}, ${alpha(theme.palette.background.paper, 0)})`,
      content: '\'\'',
      display: 'block',
      height: '100%',
      position: 'absolute',
      right: -16,
      top: 0,
      width: 15,
    }
  }
})

export function OddsGrid(props: Props) {
  const {
    bookmakers = {},
    isBookmakerLinkEnabled,
    classes,
    config,
    oddsFormat,
    dataNodes,
    inView,
    showMoreHandler,
    translations
  } = props

  const { oddsComparisonGridGameLabels = {} } = config
  const fixtureListProps = {
    data: dataNodes,
    contentId: inView,
    oddsFormat,
    showMoreHandler
  }
  return (
    <div className={classes.root} data-testid="odds-grid-container">
      <Grid className={classes.header} container wrap='nowrap'>
        <div style={{display:"flex", flexDirection:"column", justifyContent:"space-evenly"}}>          
          <Typography className={classes.gameLabel}>
            {oddsComparisonGridGameLabels[inView] || translations.game}
          </Typography>
          <Typography className={classes.signUpLabel}>
            {translations.signUp}
          </Typography>
        </div>
        <BookmakersAndLogos bookmakers={bookmakers} isBookmakerLinkEnabled={isBookmakerLinkEnabled}/>
      </Grid>
      <FixturesList {...fixtureListProps} />
    </div>
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'OddsGrid', defaultTheme: getDefaultTheme }),
  withTranslations((t: TranslationFunction) => {
    return {
      game: t('game.translation', {
        defaultValue: 'Game',
        context: 'Header label for Game inside Odds Comparison Grid widget'
      }),
      signUp: t('signUp.translation', {
        defaultValue: 'Sign up Offers',
        context: 'Header label for Sign up Offers'
      }),
      showMore: (bindings: { [key in string]: string | number }) => t('more.translation', {
        defaultValue: 'More {{competition}} games',
        context: 'Text to display on show more today button, receiving a competition parameter, inside Odds Comparison Grid widget',
        replace: bindings
      })
    }
  })
)(OddsGrid)
